import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Profile } from '../../../types/profile';
import { useAdminStore } from '../../../stores/adminStore';
import { Button } from '../../common/Button';
import ProfileAvatar from '../../common/ProfileAvatar';
import { LoadingSpinner } from '../../LoadingSpinner';
import UpdateProfileStatusModal from '../../UpdateProfileStatusModal';
import AdminProfileDetailsModal from '../modals/AdminProfileDetailsModal';

const UserRowItem = ({ profile, onClick }: { profile: Profile; onClick: () => void }) => {
  return (
    <div
      onClick={onClick}
      className="flex items-center py-3 border-b border-color-primary-inverse last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors"
    >
      <ProfileAvatar
        profile_id={profile.id}
        first_name={profile.first_name || ''}
        avatar_url={profile.avatar_url}
        className="h-10 w-10 text-xs flex-shrink-0"
      />

      <div className="ml-3 flex-1 min-w-0">
        <p className="text-sm font-bold text-secondary truncate">
          {profile.first_name} {profile.last_name}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="truncate">{profile.email}</span>
          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] border border-gray-300">
            {profile.role}
          </span>
        </div>
      </div>
    </div>
  );
};

const ProfileSection: React.FC = () => {
  const { t } = useTranslation(['common', 'admin', 'dashboard']);
  const { profiles, fetchProfiles } = useAdminStore();

  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProfiles({
        filters: { role: '', status: '', city: '', obnl_review_status: '', search: '' },
        offsetCount: 0,
        isLoadMore: false,
      });
      setLoading(false);
    };
    loadData();
  }, [fetchProfiles]);

  const recentProfiles = profiles.slice(0, 5);

  const handleRowClick = (profile: Profile) => {
    setSelectedProfile(profile);
    setIsModalOpen(true);
  };

  return (
    <>
      <section className="bg-white rounded-2xl p-4 md:p-3 flex-1 shadow-sm min-w-0 h-full flex flex-col">
        <div className="flex items-center justify-between mb-2 gap-3">
          <div className="flex items-center min-w-0 flex-1">
            <h1 className="relative text-base font-bold text-secondary flex items-center min-w-0">
              <span className="text-xl truncate">
                {t('recent_users', { ns: 'admin', defaultValue: 'Utilisateurs récents' })}
              </span>
              {recentProfiles.length > 0 && (
                <span className="ml-2 bg-primary text-white text-xs font-normal rounded-full min-w-4 h-4 flex items-center justify-center px-1 flex-shrink-0">
                  {recentProfiles.length}
                </span>
              )}
            </h1>
          </div>

          <Button
            variant="transparent-with-border"
            href="/admin/users"
            className="hidden md:flex text-xs whitespace-nowrap flex-shrink-0 ml-2"
          >
            {t('view_all_users', { ns: 'admin', defaultValue: 'Voir tous les utilisateurs' })}
          </Button>
        </div>

        <div className="flex flex-col flex-1 overflow-y-auto">
          {loading && recentProfiles.length === 0 && (
            <div className="flex justify-center items-center h-20">
              <LoadingSpinner />
            </div>
          )}

          {!loading && recentProfiles.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center">
              {t('noUsers', 'Aucun utilisateur récent')}
            </div>
          )}

          {recentProfiles.map(profile => (
            <UserRowItem
              key={profile.id || profile.email}
              profile={profile}
              onClick={() => handleRowClick(profile)}
            />
          ))}
        </div>

        <div className="mt-4 md:hidden">
          <Link
            to="/admin/users"
            className="block text-center bg-white text-secondary w-full py-2 rounded-full font-bold border border-primary shadow-sm"
          >
            {t('view_all_users', { ns: 'admin', defaultValue: 'Voir tous les utilisateurs' })}
          </Link>
        </div>
      </section>

      {isModalOpen && selectedProfile && (
        <AdminProfileDetailsModal
          onClose={() => setIsModalOpen(false)}
          profile={selectedProfile}
          onClick={() => setShowUpdateStatusModal(true)}
        />
      )}

      {showUpdateStatusModal && selectedProfile && (
        <UpdateProfileStatusModal
          user={selectedProfile}
          onClose={() => setShowUpdateStatusModal(false)}
        />
      )}
    </>
  );
};

export default ProfileSection;
